import apiService from './api.service';
import {
  SFTPCredentials,
  SFTPPasswordResetRequest,
  SFTPPasswordResetResponse,
  WebUploadResponse,
  SupportedFormatsResponse,
  UploadOptions,
} from '../types/uploads.types';

class UploadsService {
  /**
   * Get SFTP credentials for the current user.
   * Returns connection details including host, port, username, and password.
   */
  async getSFTPCredentials(): Promise<SFTPCredentials> {
    const response = await apiService.client.get('/users/me/sftp-credentials');
    return response.data;
  }

  /**
   * Reset SFTP password.
   * Requires account password confirmation for security.
   */
  async resetSFTPPassword(data: SFTPPasswordResetRequest): Promise<SFTPPasswordResetResponse> {
    const response = await apiService.client.post('/users/me/sftp-credentials/reset', data);
    return response.data;
  }

  /**
   * Get supported file formats for web upload.
   * Returns audio, archive, and CDR formats with size limits.
   */
  async getSupportedFormats(): Promise<SupportedFormatsResponse> {
    const response = await apiService.client.get('/ingestion/web-upload/formats');
    return response.data;
  }

  /**
   * Upload files via web interface.
   * Supports audio files, archives (auto-extracted), and CDR files.
   *
   * @param files Array of files to upload
   * @param options Optional agent_id and webhook_url
   * @returns Upload result with file statuses
   */
  async uploadFiles(
    files: File[],
    options?: UploadOptions
  ): Promise<WebUploadResponse> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file);
    });

    if (options?.agent_id) {
      formData.append('agent_id', options.agent_id);
    }

    if (options?.language) {
      formData.append('language', options.language);
    }

    if (options?.webhook_url) {
      formData.append('webhook_url', options.webhook_url);
    }

    const response = await apiService.client.post('/ingestion/web-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (options?.onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          options.onProgress({
            totalFiles: files.length,
            uploadedFiles: Math.floor((progress / 100) * files.length),
            currentFileProgress: progress,
            overallProgress: progress,
          });
        }
      },
    });

    return response.data;
  }

  /**
   * Cancel an ongoing upload.
   * Note: This requires tracking the upload request to cancel it.
   */
  async cancelUpload(jobId: string): Promise<void> {
    // TODO: Implement cancel endpoint if needed
    console.warn('Cancel upload not yet implemented for job:', jobId);
  }
}

export default new UploadsService();
